import { FormSubmissionSuccess } from '../../Utils/formTracking';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { bizLink } from '../../Types/bizLink';
import { ObjectType } from '../../Types/base';
import { ContainerData, ModuleData, ModuleClickAction } from '../../Types/containerV2';
import { isSegmentModuleContent } from '../../Types/containerV2/modules/segment';
import { EngageStatus, logUserStatus } from '../../Services/engageTracking';
import { useOptionalEventContext } from '../../Contexts/eventContext';
import { getPathWithRootPath } from '../../Services/common';
import { collectSelectionOptions, getSelectionErrors, resolveSelectionLink } from '../../Utils/containerSelections';

type FinishedModuleState = {
	isPassed?: boolean;
};

type ContainerContextValue = {
	containerId?: number;
	registerSegmentContent: (moduleId: number, module: ModuleData | null) => void;
	isActionDisabled: (action: ModuleClickAction) => boolean;
	isValueSelected: (group: string, value: string) => boolean;
	/** Returns whether a segment is currently expanded. */
	isSegmentExpanded: (segmentGuid: string) => boolean;
	/** Toggles the expanded state of a segment. */
	toggleSegment: (segmentGuid: string) => void;
	/** Returns whether a module click action type is enabled in the current container context. */
	isClickActionEnabled: (actionType: ModuleClickAction['type']) => boolean;
	/** Dispatches a module click action (link, expand, scroll). Respects enabledClickActions filtering. */
	handleModuleAction: (action: ModuleClickAction) => void;
	/** The type of referer for the container. */
	objectType?: ObjectType;
	/** The ID of the referer for the container. */
	objectId?: number;
	/** Marks a finishable module as finished. */
	finishModule: (moduleId: number, isPassed?: boolean) => void;
	/** Returns whether a finishable module has been finished. */
	isModuleFinished: (moduleId: number) => boolean;
	/** Returns the pass state for a finished module, or undefined if not passable or not finished. */
	getModulePassedState: (moduleId: number) => boolean | undefined;
	/** Optional admin-only inline editor hook for text modules. */
	inlineTextEditor?: InlineTextEditorConfig;
	/** Render media without playback controls while selecting modules in the editor. */
	isEditing?: boolean;
	passwordPreview?: 'locked' | 'unlocked';
	onFormSubmissionSuccess?: (event: FormSubmissionSuccess) => void;
};

const ContainerContext = createContext<ContainerContextValue | null>(null);

export type InlineTextEditorConfig = {
	activeModuleGuid: string | null;
	renderEditor: (params: { module: ModuleData; content: string }) => React.ReactNode;
};

type InitialState = {
	expandedSegments: Record<string, boolean>;
	finishableModuleIds: Set<number>;
	requiredFinishableModuleIds: Set<number>;
	finishedModules: Map<number, FinishedModuleState>;
};

/**
 * Single walk over the module tree to collect both initial expanded segments
 * and the sets of finishable module IDs.
 */
function collectInitialState(container: ContainerData): InitialState {
	const expandedSegments: Record<string, boolean> = {};
	const finishableModuleIds = new Set<number>();
	const requiredFinishableModuleIds = new Set<number>();
	const finishedModules = new Map<number, FinishedModuleState>();

	const walk = (modules: ModuleData[]) => {
		for (const m of modules) {
			if (m.moduleType === 'Segment' && isSegmentModuleContent(m.content)) {
				if (m.content.expandable.enabled) {
					expandedSegments[m.guid] = m.content.expandable.expandedByDefault;
				}
			}

			if (m.isFinishable) {
				finishableModuleIds.add(m.id);
				if (!m.isOptional) {
					requiredFinishableModuleIds.add(m.id);
				}

				if (m.userStatus?.hasFinishedBefore) {
					finishedModules.set(m.id, {});
				}
			}

			walk(m.children);
		}
	};

	for (const p of container.placeholders) {
		walk(p.modules);
	}

	return { expandedSegments, finishableModuleIds, requiredFinishableModuleIds, finishedModules };
}

function setContainerFinished(objectId?: number, objectType?: ObjectType, anyFailed?: boolean) {
	if (!objectId || !objectType) {
		return;
	}

	const status = anyFailed ? EngageStatus.Failed : EngageStatus.Finished;
	logUserStatus(objectId, objectType, status, !anyFailed);
}

type ContainerContextProviderProps = {
	container?: ContainerData;
	children: React.ReactNode;
	objectType?: ObjectType;
	objectId?: number;
	clickHandler?: (link: bizLink) => void;
	/** Enables finish-state tracking and completion reporting. Disable in admin previews. */
	trackUserProgress?: boolean;
	/** When set, only the listed action types will fire on module click. Omit to allow all. */
	enabledClickActions?: ModuleClickAction['type'][];
	/** Optional admin-only inline editor hook for text modules. */
	inlineTextEditor?: InlineTextEditorConfig;
	/** Render media without playback controls while selecting modules in the editor. */
	isEditing?: boolean;
	passwordPreview?: 'locked' | 'unlocked';
	onFormSubmissionSuccess?: (event: FormSubmissionSuccess) => void;
};

function resolveNavigableHref(href: string) {
	if (/^(https?:|mailto:|tel:|#)/i.test(href)) {
		return href;
	}

	return getPathWithRootPath(href);
}

export const ContainerContextProvider = ({
	container,
	children,
	objectType,
	objectId,
	clickHandler,
	trackUserProgress = true,
	enabledClickActions,
	inlineTextEditor,
	isEditing,
	passwordPreview,
	onFormSubmissionSuccess,
}: ContainerContextProviderProps) => {
	const eventContext = useOptionalEventContext();
	const [unlockedSegments, setUnlockedSegments] = useState<Record<number, ModuleData>>({});
	const registerSegmentContent = useCallback((moduleId: number, module: ModuleData | null) => {
		setUnlockedSegments((previous) => {
			if ((previous[moduleId] ?? null) === module) return previous;
			const next = { ...previous };
			if (module) next[moduleId] = module;
			else delete next[moduleId];
			return next;
		});
	}, []);
	const hydratedContainer = useMemo(() => {
		if (!container) return undefined;
		const hydrate = (modules: ModuleData[]): ModuleData[] =>
			modules.map((module) => {
				const current = unlockedSegments[module.id] ?? module;
				return { ...current, children: hydrate(current.children) };
			});
		return { ...container, placeholders: container.placeholders.map((p) => ({ ...p, modules: hydrate(p.modules) })) };
	}, [container, unlockedSegments]);
	const selectionOptions = useMemo(() => collectSelectionOptions(hydratedContainer), [hydratedContainer]);
	const selectionKey = `${container?.id}:${objectType}:${objectId}`;
	const [selectionState, setSelectionState] = useState<{ key: string; values: Map<string, string> }>(() => ({
		key: selectionKey,
		values: new Map(),
	}));
	useEffect(() => {
		setSelectionState({ key: selectionKey, values: new Map() });
	}, [selectionKey]);
	const selectedValues = useMemo(
		() =>
			new Map(
				selectionState.key === selectionKey
					? [...selectionState.values].filter(([group, value]) =>
							selectionOptions.some((option) => option.group === group && option.value === value)
						)
					: []
			),
		[selectionState, selectionKey, selectionOptions]
	);
	const isValueSelected = useCallback((group: string, value: string) => selectedValues.get(group) === value, [selectedValues]);
	const isActionDisabled = useCallback(
		(action: ModuleClickAction) =>
			getSelectionErrors(action, selectionOptions).length > 0 ||
			(action.type === 'link' && !resolveSelectionLink(action, selectedValues)),
		[selectionOptions, selectedValues]
	);
	const initialState = useMemo(() => (hydratedContainer ? collectInitialState(hydratedContainer) : null), [hydratedContainer]);
	const reportedCompletionStateRef = useRef<string | null>(null);

	const [expandedSegments, setExpandedSegments] = useState<Record<string, boolean>>(() => initialState?.expandedSegments ?? {});
	const [finishedModules, setFinishedModules] = useState<Map<number, FinishedModuleState>>(
		() => initialState?.finishedModules ?? new Map()
	);

	const isSegmentExpanded = useCallback(
		(segmentGuid: string) => expandedSegments[segmentGuid] ?? initialState?.expandedSegments[segmentGuid] ?? false,
		[expandedSegments, initialState]
	);

	const toggleSegment = useCallback(
		(segmentGuid: string) => {
			setExpandedSegments((prev) => ({
				...prev,
				[segmentGuid]: !(prev[segmentGuid] ?? initialState?.expandedSegments[segmentGuid] ?? false),
			}));
		},
		[initialState]
	);

	const scrollToSegment = useCallback((segmentGuid: string) => {
		const el = document.querySelector(`[data-module-guid="${CSS.escape(segmentGuid)}"]`);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, []);

	const finishModule = useCallback(
		(moduleId: number, isPassed?: boolean) => {
			if (!trackUserProgress) return;
			if (!initialState?.finishableModuleIds.has(moduleId)) return;
			setFinishedModules((prev) => {
				const currentState = prev.get(moduleId);
				if (currentState && currentState.isPassed === isPassed) return prev;

				const next = new Map(prev);
				next.set(moduleId, { isPassed });
				return next;
			});
		},
		[initialState, trackUserProgress]
	);

	const isClickActionEnabled = useCallback(
		(actionType: ModuleClickAction['type']) => !enabledClickActions || enabledClickActions.includes(actionType),
		[enabledClickActions]
	);

	const handleModuleAction = useCallback(
		(action: ModuleClickAction) => {
			if (!isClickActionEnabled(action.type) || isActionDisabled(action)) return;

			switch (action.type) {
				case 'select-value':
					setSelectionState((previous) => ({
						key: selectionKey,
						values: new Map(previous.key === selectionKey ? previous.values : []).set(action.group, action.value),
					}));
					break;
				case 'dispatch-events':
					action.eventNames.forEach((eventName) => eventContext?.dispatch(eventName));
					break;
				case 'finish-module':
					finishModule(action.moduleId);
					break;
				case 'expand-segment':
					toggleSegment(action.segmentGuid);
					break;
				case 'scroll-to-segment':
					scrollToSegment(action.segmentGuid);
					break;
				case 'link': {
					const link = resolveSelectionLink(action, selectedValues);
					if (!link) return;
					if (clickHandler) {
						clickHandler(link);
						break;
					}

					if (link.type === 'url' && link.href) {
						const href = resolveNavigableHref(link.href);

						if (link.target === '_blank') {
							window.open(href, '_blank', 'noopener,noreferrer');
						} else {
							window.location.href = href;
						}
					}
					break;
				}
			}
		},
		[
			isClickActionEnabled,
			isActionDisabled,
			selectionKey,
			selectedValues,
			toggleSegment,
			scrollToSegment,
			clickHandler,
			eventContext,
			finishModule,
		]
	);

	const isModuleFinished = useCallback((moduleId: number) => finishedModules.has(moduleId), [finishedModules]);

	const getModulePassedState = useCallback((moduleId: number) => finishedModules.get(moduleId)?.isPassed, [finishedModules]);

	useEffect(() => {
		if (!trackUserProgress || !initialState) {
			reportedCompletionStateRef.current = null;
			return;
		}

		const requiredFinishableModuleIds = Array.from(initialState.requiredFinishableModuleIds);
		const allFinished = requiredFinishableModuleIds.every((moduleId) => finishedModules.has(moduleId));

		if (!allFinished) {
			reportedCompletionStateRef.current = null;
			return;
		}

		const anyFailed = requiredFinishableModuleIds.some((moduleId) => finishedModules.get(moduleId)?.isPassed === false);
		const completionStateKey = `${objectType ?? 'none'}:${objectId ?? 'none'}:${anyFailed ? 'failed' : 'finished'}`;

		if (reportedCompletionStateRef.current === completionStateKey) {
			return;
		}

		setContainerFinished(objectId, objectType, anyFailed);
		reportedCompletionStateRef.current = completionStateKey;
	}, [initialState, finishedModules, objectId, objectType, trackUserProgress]);

	const value = useMemo(
		() => ({
			containerId: container?.id,
			registerSegmentContent,
			passwordPreview,
			isActionDisabled,
			isValueSelected,
			isSegmentExpanded,
			toggleSegment,
			isClickActionEnabled,
			handleModuleAction,
			objectType,
			objectId,
			finishModule,
			isModuleFinished,
			getModulePassedState,
			inlineTextEditor,
			isEditing,
			onFormSubmissionSuccess,
		}),
		[
			container?.id,
			registerSegmentContent,
			passwordPreview,
			isActionDisabled,
			isValueSelected,
			isSegmentExpanded,
			toggleSegment,
			isClickActionEnabled,
			handleModuleAction,
			objectType,
			objectId,
			finishModule,
			isModuleFinished,
			getModulePassedState,
			inlineTextEditor,
			isEditing,
			onFormSubmissionSuccess,
		]
	);

	return <ContainerContext.Provider value={value}>{children}</ContainerContext.Provider>;
};

export const useContainerContext = (): ContainerContextValue => {
	const ctx = useContext(ContainerContext);
	if (!ctx) {
		throw new Error('useContainerContext must be used within a ContainerContextProvider');
	}
	return ctx;
};
